/**
 * Mock Data Provider
 *
 * Safeguarded access to mock data - only returns data in MOCK MODE.
 * In demo/production modes, returns empty arrays or throws errors.
 *
 * This prevents accidental use of mock data in non-mock environments.
 */

import { mode } from '@/lib/config/app-mode';
import { APP_MODE } from '@/lib/config/app-mode';
import type { StartupProfile } from '@/lib/state/profile-store';
import type { ScoreCriteria, AIRecommendation } from '@/lib/scoring/types';
import type {
  InvestorProfile,
  DealflowStartup,
  PortfolioCompany,
  DataRoomDocument,
  InvestorMatch,
  UserInfo,
  VerificationRequest,
  InvestorCommitment,
  ExternalCommitment,
} from './inverio-store';

// Import mock data module - this will be tree-shaken in production
import * as mockDataModule from './mock-data';

/**
 * Throws error if not in mock mode
 */
function assertMockMode(operation: string): void {
  if (!mode.showSampleData) {
    throw new Error(
      `[MockData] Attempted to use mock data for "${operation}" in ${APP_MODE.toUpperCase()} mode. ` +
      `Mock data is only available in MOCK mode. Set EXPO_PUBLIC_APP_MODE=mock to enable.`
    );
  }
}

// ============================================================================
// SAFE MOCK DATA ACCESSORS
// These return empty arrays/objects in non-mock modes instead of throwing
// ============================================================================

/**
 * Get mock startups - returns empty array in non-mock modes
 */
export function getMockStartups(): StartupProfile[] {
  if (!mode.showSampleData) return [];
  return mockDataModule.mockStartups ?? [];
}

/**
 * Get mock startup by ID - returns undefined in non-mock modes
 */
export function getMockStartupById(id: string): StartupProfile | undefined {
  if (!mode.showSampleData) return undefined;
  return mockDataModule.mockStartups?.find(s => s.id === id);
}

/**
 * Get mock investor users - returns empty object in non-mock modes
 */
export function getMockInvestorUsers(): Record<string, UserInfo> {
  if (!mode.showSampleData) return {};
  return mockDataModule.mockInvestorUsers ?? {};
}

/**
 * Get mock investor profiles - returns empty array in non-mock modes
 */
export function getMockInvestorProfiles(): InvestorProfile[] {
  if (!mode.showSampleData) return [];
  return mockDataModule.mockInvestors ?? [];
}

/**
 * Get mock investor profile by ID - returns undefined in non-mock modes
 */
export function getMockInvestorById(id: string): InvestorProfile | undefined {
  if (!mode.showSampleData) return undefined;
  return mockDataModule.mockInvestors?.find(i => i.id === id);
}

/**
 * Get mock dealflow - returns empty array in non-mock modes
 */
export function getMockDealflow(): DealflowStartup[] {
  if (!mode.showSampleData) return [];
  return mockDataModule.sampleDealflow ?? [];
}

/**
 * Get mock portfolio companies - returns empty array in non-mock modes
 */
export function getMockPortfolio(): PortfolioCompany[] {
  if (!mode.showSampleData) return [];
  return mockDataModule.samplePortfolio ?? [];
}

/**
 * Get mock data room documents (flat list) - returns empty array in non-mock modes
 */
export function getMockDataRoomDocuments(): DataRoomDocument[] {
  if (!mode.showSampleData) return [];
  return mockDataModule.sampleDataRoomDocuments ?? [];
}

/**
 * Get mock data room documents for a specific startup - returns empty array in non-mock modes
 * Uses the per-startup keyed map from mock-enrichments.
 */
export function getMockDataRoomDocumentsByStartup(startupId: string): DataRoomDocument[] {
  if (!mode.showSampleData) return [];
  return mockDataModule.mockDataRoomDocuments?.[startupId] ?? [];
}

/**
 * Get mock AI recommendations - returns empty array in non-mock modes
 */
export function getMockRecommendations(): AIRecommendation[] {
  if (!mode.showSampleData) return [];
  return mockDataModule.sampleAIRecommendations ?? [];
}

/**
 * Get mock investor matches - returns empty array in non-mock modes
 */
export function getMockInvestorMatches(): InvestorMatch[] {
  if (!mode.showSampleData) return [];
  return mockDataModule.sampleInvestorMatches ?? [];
}

/**
 * Get mock score criteria with realistic scores - returns empty array in non-mock modes
 */
export function getMockScoreCriteria(): ScoreCriteria[] {
  if (!mode.showSampleData) return [];
  return mockDataModule.getSampleScoredCriteria?.() ?? mockDataModule.defaultScoreCriteria ?? [];
}

/**
 * Get mock verification requests - returns empty array in non-mock modes
 */
export function getMockVerificationRequests(): VerificationRequest[] {
  if (!mode.showSampleData) return [];
  return mockDataModule.sampleVerificationRequests ?? [];
}

/**
 * Get enhanced investor profile - returns undefined in non-mock modes
 */
export function getMockEnhancedInvestorProfile(): InvestorProfile | undefined {
  if (!mode.showSampleData) return undefined;
  return mockDataModule.mockEnhancedInvestorProfile;
}

/**
 * Get mock commitments - returns empty array in non-mock modes
 */
export function getMockCommitments(): InvestorCommitment[] {
  if (!mode.showSampleData) return [];
  return mockDataModule.sampleCommitments ?? [];
}

/**
 * Get mock external commitments - returns empty array in non-mock modes
 */
export function getMockExternalCommitments(): ExternalCommitment[] {
  if (!mode.showSampleData) return [];
  return mockDataModule.sampleExternalCommitments ?? [];
}

/**
 * Get mock data-room access requests - returns empty array in non-mock modes.
 */
export function getMockAccessRequests(): import('./mock-data').SampleAccessRequest[] {
  if (!mode.showSampleData) return [];
  return mockDataModule.sampleAccessRequests ?? [];
}

// ============================================================================
// STRICT MOCK DATA ACCESSORS
// These throw errors in non-mock modes - use when mock data is required
// ============================================================================

/**
 * Get mock startups (strict) - throws in non-mock modes
 */
export function requireMockStartups(): StartupProfile[] {
  assertMockMode('startups');
  return mockDataModule.mockStartups ?? [];
}

/**
 * Get mock investor profiles (strict) - throws in non-mock modes
 */
export function requireMockInvestorProfiles(): InvestorProfile[] {
  assertMockMode('investorProfiles');
  return mockDataModule.mockInvestors ?? [];
}

/**
 * Get mock dealflow (strict) - throws in non-mock modes
 */
export function requireMockDealflow(): DealflowStartup[] {
  assertMockMode('dealflow');
  return mockDataModule.sampleDealflow ?? [];
}

// ============================================================================
// SIMULATED RESPONSES FOR MOCK MODE
// ============================================================================

/**
 * Simulated AI response for DD Chat in mock mode
 */
export function getMockAIResponse(query: string): string {
  if (!mode.showSampleData) {
    throw new Error('[MockData] getMockAIResponse should only be called in mock mode');
  }

  const lowerQuery = query.toLowerCase();

  if (lowerQuery.includes('market') || lowerQuery.includes('tam')) {
    return `Based on the available data, this startup operates in a rapidly growing market estimated at $45B globally. The serviceable addressable market (SAM) for their specific niche is approximately $8B, with strong growth projections of 15-20% CAGR over the next 5 years.

**Key Market Insights:**
- Growing demand driven by digital transformation
- Underserved SMB segment presents opportunity
- Regulatory tailwinds in target markets

*Note: This is simulated analysis for demonstration purposes.*`;
  }

  if (lowerQuery.includes('team') || lowerQuery.includes('founder')) {
    return `The founding team brings relevant experience to this venture:

**Team Highlights:**
- Previous successful exit (acqui-hired by Salesforce in 2021)
- Deep domain expertise in AI/ML
- Complementary skill sets across technical and business functions
- Strong advisory board including industry veterans

*Note: This is simulated analysis for demonstration purposes.*`;
  }

  if (lowerQuery.includes('traction') || lowerQuery.includes('revenue') || lowerQuery.includes('growth')) {
    return `The startup shows promising early traction metrics:

**Traction Summary:**
- MRR: $18,000 (growing 15% MoM)
- Active Users: 1,000+
- Customer Retention: Strong (specific NRR data pending)
- Growth trajectory suggests product-market fit

*Note: This is simulated analysis for demonstration purposes.*`;
  }

  if (lowerQuery.includes('risk') || lowerQuery.includes('concern')) {
    return `Based on the analysis, key risks and considerations include:

**Risk Assessment:**
1. **Market Competition** - Competing with well-funded incumbents (Zapier, Make)
2. **Execution Risk** - Scaling team while maintaining culture
3. **Technical Risk** - AI model accuracy at scale
4. **Capital Efficiency** - Burn rate optimization needed

**Mitigating Factors:**
- Experienced team with prior exits
- Differentiated technology approach
- Strong early customer feedback

*Note: This is simulated analysis for demonstration purposes.*`;
  }

  // Default response
  return `Thank you for your question. Based on the available information:

This startup demonstrates characteristics typical of early-stage ventures in the Enterprise SaaS space. Their approach to AI-powered workflow automation addresses a real pain point for SMBs.

**Key Observations:**
- Clear problem-solution fit
- Growing market opportunity
- Experienced founding team
- Early but promising traction

Would you like me to dive deeper into any specific aspect of the analysis?

*Note: This is simulated analysis for demonstration purposes. In demo/production mode, this would provide real AI-powered analysis based on indexed documents.*`;
}

/**
 * Simulated authentication for mock mode
 */
export function getMockUser(email: string): { id: string; email: string; role: 'investor' | 'startup' } | null {
  if (!mode.showSampleData) {
    throw new Error('[MockData] getMockUser should only be called in mock mode');
  }

  // Check mock investor users
  const investorEntry = Object.entries(mockDataModule.mockInvestorUsers ?? {}).find(
    ([_, user]) => user.email.toLowerCase() === email.toLowerCase()
  );
  if (investorEntry) {
    return { id: investorEntry[0], email: investorEntry[1].email, role: 'investor' };
  }

  // Check mock startup (founders)
  const startup = mockDataModule.mockStartups?.find(
    s => s.founderEmail?.toLowerCase() === email.toLowerCase()
  );
  if (startup && startup.founderEmail) {
    return { id: startup.id, email: startup.founderEmail, role: 'startup' };
  }

  // Demo accounts for easy testing
  if (email.endsWith('@mock.inverio.ai')) {
    const role = email.includes('investor') ? 'investor' : 'startup';
    return { id: `mock_${Date.now()}`, email, role };
  }

  return null;
}

/**
 * Check if mock mode is active
 */
export function isMockModeActive(): boolean {
  return mode.showSampleData;
}
